/**
 * 位置数据优化工具
 * 用于减少 GetAllLastLocations API 的数据传输和处理
 */

class LocationDataOptimizer {
  constructor() {
    // 上次数据的快照
    this.lastSnapshot = new Map()
    // 数据版本号
    this.version = 0
    // 数据压缩配置
    this.compressionConfig = {
      removeNullFields: true,
      simplifyCoordinates: true,
      coordinatePrecision: 6
    }
  }

  /**
   * 比较并更新数据，只处理有变化的部分
   * @param {Array} newData 新的设备数据
   * @returns {Object} 包含变化的数据和统计信息
   */
  processIncrementalUpdate(newData) {
    const changes = {
      added: [],
      updated: [],
      removed: [],
      unchanged: 0
    }

    const currentIds = new Set()

    newData.forEach(item => {
      if (!item.tracker) return

      const id = item.tracker.sssid
      currentIds.add(id)

      const oldItem = this.lastSnapshot.get(id)

      if (!oldItem) {
        // 新增设备
        changes.added.push(item)
        this.lastSnapshot.set(id, this.cloneItem(item))
      } else if (this.hasChanged(oldItem, item)) {
        // 设备数据有变化
        changes.updated.push(item)
        this.lastSnapshot.set(id, this.cloneItem(item))
      } else {
        // 数据未变化
        changes.unchanged++
      }
    })

    // 查找已删除的设备
    this.lastSnapshot.forEach((value, key) => {
      if (!currentIds.has(key)) {
        changes.removed.push(key)
        this.lastSnapshot.delete(key)
      }
    })

    this.version++

    return changes
  }

  /**
   * 检查设备数据是否有变化
   * @param {Object} oldItem 旧数据
   * @param {Object} newItem 新数据
   * @returns {boolean} 是否有变化
   */
  hasChanged(oldItem, newItem) {
    // 检查关键字段是否变化
    const checkFields = [
      // 位置信息
      'lastLoc.latitude',
      'lastLoc.longitude',
      'lastLoc.speed',
      'lastLoc.direction',
      'lastLoc.online',
      'lastLoc.receivedTime',
      // 设备信息
      'tracker.name',
      'tracker.color',
      'tracker.locked',
      // 报警信息
      'lastAlarm'
    ]

    for (const field of checkFields) {
      if (this.getNestedValue(oldItem, field) !== this.getNestedValue(newItem, field)) {
        return true
      }
    }

    return false
  }

  /**
   * 获取嵌套对象的值
   * @param {Object} obj 对象
   * @param {string} path 属性路径
   */
  getNestedValue(obj, path) {
    const keys = path.split('.')
    let value = obj

    for (const key of keys) {
      if (value == null) return null
      value = value[key]
    }

    return value
  }

  /**
   * 克隆设备数据（浅拷贝关键字段）
   */
  cloneItem(item) {
    return {
      tracker: {
        sssid: item.tracker?.sssid,
        name: item.tracker?.name,
        color: item.tracker?.color,
        locked: item.tracker?.locked
      },
      lastLoc: item.lastLoc ? {
        latitude: item.lastLoc.latitude,
        longitude: item.lastLoc.longitude,
        speed: item.lastLoc.speed,
        direction: item.lastLoc.direction,
        online: item.lastLoc.online,
        receivedTime: item.lastLoc.receivedTime
      } : null,
      lastAlarm: item.lastAlarm
    }
  }

  /**
   * 压缩数据，移除不必要的字段
   * @param {Array} data 原始数据
   * @returns {Array} 压缩后的数据
   */
  compressData(data) {
    return data.map(item => {
      const compressed = {}

      // 只保留必要的 tracker 字段
      if (item.tracker) {
        compressed.tracker = {
          sssid: item.tracker.sssid,
          name: item.tracker.name,
          imei: item.tracker.imei,
          typeName: item.tracker.typeName,
          objectTypeId: item.tracker.objectTypeId,
          protocol: item.tracker.protocol,
          color: item.tracker.color,
          icon: item.tracker.icon || item.tracker.image,
          locked: item.tracker.locked
        }
      }

      // 只保留必要的 lastLoc 字段
      if (item.lastLoc) {
        compressed.lastLoc = {
          // 位置信息
          latitude: this.roundCoordinate(item.lastLoc.latitude),
          longitude: this.roundCoordinate(item.lastLoc.longitude),
          speed: item.lastLoc.speed,
          direction: item.lastLoc.direction,
          address: item.lastLoc.address,

          // GPS/GSM
          gpsAvailable: item.lastLoc.gpsAvailable,
          gsmLatitude: item.lastLoc.gsmLatitude,
          gsmLongitude: item.lastLoc.gsmLongitude,
          stationID: item.lastLoc.stationID,

          // 状态
          online: item.lastLoc.online,
          receivedTime: item.lastLoc.receivedTime,

          // 只在需要时包含的字段
          ...(item.lastLoc.battery != null && { battery: item.lastLoc.battery }),
          ...(item.lastLoc.gsmsignal != null && { gsmsignal: item.lastLoc.gsmsignal })
        }
      }

      // 报警信息
      if (item.lastAlarm !== null) {
        compressed.lastAlarm = item.lastAlarm
      }

      return compressed
    })
  }

  /**
   * 精简坐标精度
   */
  roundCoordinate(coord) {
    if (coord == null) return coord
    return Math.round(coord * 1000000) / 1000000 // 6位小数精度
  }

  /**
   * 获取数据统计信息
   */
  getStats() {
    return {
      totalDevices: this.lastSnapshot.size,
      dataVersion: this.version,
      lastUpdateTime: new Date().toISOString()
    }
  }

  /**
   * 清除缓存
   */
  clear() {
    this.lastSnapshot.clear()
    this.version = 0
  }
}

export default new LocationDataOptimizer()